import React, { useEffect, useState } from 'react';
import {
  ChallengeStatus,
  MFAAction,
  type PushNotificationMetadata,
} from '@/api/generated';
import type { UseMfaOrchestratorReturn } from '@/hooks/use-mfa-orchestrator.types';
import { useMfaPolling } from '@/hooks/use-mfa-polling';
import { useMfaResend } from '@/hooks/use-mfa-resend';
import { useResendDeadline } from '@/hooks/use-resend-countdown';
import { resendPushChallenge } from '@/services/apis/mfa-orchestrator.api';
import { PushNotificationPollingApprovedState } from './push-notification-polling-approved-state';
import { PushNotificationPollingDeclinedState } from './push-notification-polling-declined-state';
import { PushNotificationPollingErrorState } from './push-notification-polling-error-state';
import { PushNotificationPollingPendingState } from './push-notification-polling-pending-state';

interface PushNotificationPollingProps {
  submitResponse: UseMfaOrchestratorReturn['submitResponse'];
  // Metadata from FactorStepResponse - cast to PushNotificationMetadata for this factor
  metadata?: PushNotificationMetadata;
  hasAlternatives?: boolean;
  onShowAlternatives?: () => void;
  username?: string;
  onBack?: () => void;
  isEscalationFlow?: boolean;
}

/**
 * Push Notification Polling Component
 * Shows when user is waiting for approval from another device
 * Used when redirecting from the login page
 */
export const PushNotificationPolling: React.FC<
  PushNotificationPollingProps
> = ({
  submitResponse,
  metadata: initialMetadata,
  hasAlternatives,
  onShowAlternatives,
  username,
  onBack,
  isEscalationFlow,
}) => {
  // Track metadata state (can be updated by resend)
  const [metadata, setMetadata] = useState<
    PushNotificationMetadata | undefined
  >(initialMetadata);

  const {
    state,
    startPolling,
    retry: retryPolling,
  } = useMfaPolling({
    pollingInterval: 2000,
    maxRetries: 30,
    onStatusChange: async status => {
      // When status changes from pending, notify parent
      if (status !== ChallengeStatus.PENDING) {
        await submitResponse({
          action:
            status === ChallengeStatus.APPROVED
              ? MFAAction.APPROVE
              : MFAAction.REJECT,
        });
      }
    },
    onError: error => {
      console.error('Polling error:', error);
    },
  });

  const { deadline, setDeadline, isResolving } = useResendDeadline(
    initialMetadata?.next_resend_at
  );

  const { resend, isResending } = useMfaResend({
    resendRequest: resendPushChallenge,
    onResendSuccess: fresh => {
      // A resend regenerates the visual-match set, so the images move with it.
      setMetadata(fresh);
      setDeadline(fresh.next_resend_at);
    },
  });

  // Start polling on mount
  useEffect(() => {
    startPolling();
  }, [startPolling]);

  // Error state - check errorMessage instead of hasError
  if (state.errorMessage || state.status === ChallengeStatus.FAILED) {
    return (
      <PushNotificationPollingErrorState
        errorMessage={state.errorMessage}
        retry={retryPolling}
      />
    );
  }

  // Show state based on status
  switch (state.status) {
    case ChallengeStatus.APPROVED:
      return <PushNotificationPollingApprovedState />;

    case ChallengeStatus.REJECTED:
      return <PushNotificationPollingDeclinedState />;

    case ChallengeStatus.PENDING:
    default:
      // Polling state
      return (
        <PushNotificationPollingPendingState
          resend={resend}
          isResending={isResending}
          isResolving={isResolving}
          nextResendAt={deadline}
          metadata={metadata}
          hasAlternatives={hasAlternatives}
          onShowAlternatives={onShowAlternatives}
          username={username}
          onBack={onBack}
          isEscalationFlow={isEscalationFlow}
        />
      );
  }
};
